import type { IdentifierConfig } from './display-name';

/** Pure classification helpers for the JSON formatter (identifiers, reserved keys, timestamps, urls). */

// Unix-timestamp bounds (seconds) matching the backend's validator.
export const MIN_UNIX_TIMESTAMP = 99999999;
export const MAX_UNIX_TIMESTAMP = 10000000000;

/** A value that looks like a unix timestamp (seconds): all-digit integer within the bounds. */
export function isValidTimestamp(value: unknown): boolean {
  const s = String(value);
  if (/\D/.test(s)) return false;
  const n = Number(s);
  return Number.isInteger(n) && n > MIN_UNIX_TIMESTAMP && n < MAX_UNIX_TIMESTAMP;
}

/** Reserved person attributes / event attributes (rendered with a "Reserved" badge). */
export const RESTRICTED_ATTRIBUTES = [
  'id',
  'email',
  '_created_in_customerio_at',
  'anonymous_id',
  'created_at',
  'email_sha256',
  'mobile_ad_id',
  'mobile_ad_id_sha256',
  'unsubscribed',
  'cio_test_user',
  'cio_double_opt_in',
  'timezone',
  'timezone_valid',
];
export const RESTRICTED_EVENT_ATTRIBUTES = ['from_address', 'reply_to', 'recipient', 'form_submit'];

const OBJECT_IDENTIFIER_KEYS = ['cio_object_id', 'object_id'];

/** Types whose root keys can be identifiers. */
const IDENTIFIER_LOG_TYPES = ['attribute_change', 'profile_create', 'profile_delete', 'profile_merge', 'segment_build'];

/** Whether this log type surfaces "Identifier" badges (keyed off the raw `log.type`). */
export function containsIdentifiersForLogType(type: string): boolean {
  return IDENTIFIER_LOG_TYPES.includes(type);
}

/** Reserved-attribute list for this log type. */
export function reservedAttributesForLogType(type: string): string[] {
  if (type === 'attribute_change') return RESTRICTED_ATTRIBUTES;
  if (type === 'event') return RESTRICTED_EVENT_ATTRIBUTES;
  return [];
}

/** Whether an attribute name is an identifier (identifier config first, object keys as fallback). */
export function isIdentifierAttr(name: string, identifiers: IdentifierConfig): boolean {
  if (name in identifiers) return identifiers[name] !== 0;
  return OBJECT_IDENTIFIER_KEYS.includes(name);
}

/** URL detection for string values (rendered as links). */
export function isUrl(value: string): boolean {
  return /^(http|https):\/\/[^\s]+$/.test(value);
}

/**
 * Whether an array belongs to a segment-membership change — its numeric elements are segment ids
 * that should render as segment-name links (keyed off the parent key being `entered`/`exited`).
 */
export function isSegmentMembershipArrayKey(parentKey: string | undefined): boolean {
  return parentKey === 'entered' || parentKey === 'exited';
}

/**
 * Coerce a segment-membership array element to its numeric segment id. Ids may serialize as a
 * number or a numeric string; both should link (matching the `Number(...)` resolution used when
 * the row's segment is looked up). Returns null for anything non-numeric so it falls back to a
 * plain JSON value.
 */
export function toSegmentId(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}
